import { HomeIcon } from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import type { ReadPropertyWithOwners } from '@/services/api/subjectsService';
import { enhancedApi } from '@/services/api/subjectsService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

export type Property = ReadPropertyWithOwners;

export const PropertyTooltipContent = memo<EntityTooltipContentProps<Property>>(
  ({ fetchState }) => {
    const property = fetchState.status === 'success' ? fetchState.data : null;

    const displayName = useMemo(() => {
      if (!property) return null;
      return (
        property.property_name ||
        [property.physical_address?.line1, property.physical_address?.city]
          .filter(Boolean)
          .join(', ') ||
        'Property'
      );
    }, [property]);

    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="property" />;
    }

    if (fetchState.status === 'idle' || !property) {
      return <EmptyState entityType="property" />;
    }

    const badges =
      property.real_property_type &&
      createStatusBadge({
        value: property.real_property_type.replace(/_/g, ' '),
        defaultColor: 'zinc',
      });

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader
          icon={HomeIcon}
          title={displayName || 'Property'}
          badges={badges}
        />

        {(property.physical_address?.line1 || property.parcel_id) && (
          <TooltipSection withBorder>
            <div className="space-y-1.5 text-sm text-gray-900">
              {property.physical_address?.line1 && (
                <div>{property.physical_address.line1}</div>
              )}
              {property.parcel_id && <div>{property.parcel_id}</div>}
            </div>
          </TooltipSection>
        )}
      </div>
    );
  },
);

PropertyTooltipContent.displayName = 'PropertyTooltipContent';

export const PropertyLink = createEntityLink<
  ReadPropertyWithOwners,
  { id: string; matterId: string }
>({
  tooltipContent: PropertyTooltipContent,
  useQuery: enhancedApi.endpoints.propertiesWithOwners.useQuery,
  getQueryArg: (id) => {
    const [matterId, propertyId] = id.split(':');
    return { id: propertyId || id, matterId: matterId || id };
  },
  displayName: 'PropertyLink',
});
